import { Check, Users } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Combobox } from '@apps/web/components/ui/combobox';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '@apps/web/components/ui/command';
import { type CustomFilterConfig } from '@apps/web/components/ui/data_table_toolbar';
import { useFormatPersonName } from '@apps/web/hooks/use_person_name_format';

export type ShiftFilterClient = {
    readonly id: string;
    readonly name: string;
};

export function useShiftClientFilterConfig({
    clients,
    selectedIds,
    clientReferenceTerm,
    onToggle,
    onClear,
}: {
    readonly clients: ReadonlyArray<ShiftFilterClient> | undefined;
    readonly selectedIds: ReadonlyArray<string>;
    readonly clientReferenceTerm: string;
    readonly onToggle: (clientId: string) => void;
    readonly onClear: () => void;
}): CustomFilterConfig | undefined {
    const { formatName } = useFormatPersonName();
    const [searchValue, setSearchValue] = useState('');
    const filteredClients = useMemo(() => {
        const normalizedSearch = searchValue.trim().toLowerCase();
        return (clients ?? []).filter(
            (client) =>
                !normalizedSearch ||
                client.name.toLowerCase().includes(normalizedSearch),
        );
    }, [clients, searchValue]);
    const activeLabels = useMemo(
        () =>
            selectedIds
                .map(
                    (id) =>
                        formatName(
                            clients?.find((client) => client.id === id)?.name,
                        ) || null,
                )
                .filter((name): name is string => name !== null),
        [clients, selectedIds, formatName],
    );

    if (!clients || clients.length === 0) {
        return undefined;
    }

    const title = `${clientReferenceTerm.charAt(0).toUpperCase()}${clientReferenceTerm.slice(1)}`;
    const renderOptions = () => (
        <CommandGroup>
            {filteredClients.map((client) => (
                <CommandItem
                    key={client.id}
                    value={`${client.name} ${client.id}`}
                    onSelect={() => onToggle(client.id)}
                >
                    <span className="flex-1">{formatName(client.name)}</span>
                    {selectedIds.includes(client.id) && (
                        <Check className="h-4 w-4 shrink-0" />
                    )}
                </CommandItem>
            ))}
        </CommandGroup>
    );

    return {
        id: 'client',
        title,
        icon: Users,
        selectedCount: selectedIds.length,
        activeFilterLabels: activeLabels,
        onClear,
        renderValueSelection: ({ open, onOpenChange }) => (
            <Combobox
                open={open}
                onOpenChange={onOpenChange}
                displayValue={activeLabels.join(', ')}
                placeholder=""
                searchValue={searchValue}
                onSearchValueChange={setSearchValue}
                searchPlaceholder={`Search ${clientReferenceTerm}s...`}
                triggerClassName="h-7 max-w-[16rem] min-w-[6rem] rounded-full px-3 text-xs"
                contentClassName="w-72"
                matchTriggerWidth={false}
                fullWidth={false}
                commandProps={{ filter: () => 1 }}
            >
                <CommandEmpty>No clients found.</CommandEmpty>
                {renderOptions()}
            </Combobox>
        ),
        children: (
            <Command filter={() => 1} className="max-h-72 overflow-hidden">
                <CommandInput
                    placeholder={`Search ${clientReferenceTerm}s...`}
                    value={searchValue}
                    onValueChange={setSearchValue}
                />
                <CommandList className="max-h-56">
                    <CommandEmpty>No clients found.</CommandEmpty>
                    {renderOptions()}
                </CommandList>
            </Command>
        ),
    };
}
